import * as React from 'react';
import DiagnosticsTrigger from 'mini-diagnostics/lib/components/DiagnosticsTrigger';
import FolderPaneTree from './FolderPaneTree';
import FolderTreeType from 'mini-mail-folder-store/lib/store/schema/FolderTreeType';
import getMailRootFolderIds from 'mini-mail-folder-store/lib/utils/getMailRootFolderIds';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isItemView from 'owa-mail-store/lib/utils/isItemView';
import Module from 'mini-module-switcher/lib/schema/Module';
import MoveToFolderPaneTree from './MoveToFolderPaneTree';
import openLeftNavPerfDatapointInitializer from 'mini-mail-list-store/lib/mutators/openLeftNavPerfDatapointInitialize';
import Overlay from 'mini-overlay/lib/components/Overlay';
import setShowFolderPane from 'mini-mail-folder-store/lib/actions/setShowFolderPane';
import SidePaneHeader from 'mini-sidepane-header';
import store from 'mini-mail-folder-store/lib/store/Store';
import strings from 'owa-strings/owa-mini-strings';
import switchModule from 'owa-mail-store/lib/actions/switchModule';
import toggleMoveToFolderPane from 'mini-mail-folder-store/lib/actions/toggleMoveToFolderPane';
import { escapeKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { FocusTrapZone } from 'focus-trap-zone';
import { getHeaderAriaLabel, getHeaderTextWithSelectedItemsCount } from 'mini-mail-header-view';
import { getStore as getFolderStore } from 'mini-mail-folder-store/lib/store/Store';
import { Header, HeaderIcon } from 'mini-header';
import { isDevToolsEnabled } from 'mini-diagnostics/lib/utils/isDevToolsEnabled';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { lazyShowHideReportErrorForm } from 'mini-report-error';
import { logUsage } from 'owa-analytics';
import { mergeStyles } from '@uifabric/merge-styles';
import { MiniIcons } from 'owa-mini-icons';
import { MiniWorkLoad } from 'mini-workload';
import { observer } from 'mobx-react';
import { PortalWrapper } from 'portal-wrapper';
import { preventModalScroll } from 'prevent-modal-scroll';
import { shouldShowFeedbackLink } from 'mini-sidepane-footer/lib/utils/shouldShowFeedbackLink';
import { SidePaneFooter } from 'mini-sidepane-footer';

const styles = require('./FolderPane.scss');
const classNames = require('classnames/bind').bind(styles);

const DEBOUNCE_DELAY = 100;
const RESIZE_EVENT = 'resize';

// Defines interface for FolderPaneState
export interface FolderPaneState {
    currentHeight: number;
}

export interface FolderPaneProps {
    /**
     * Function passed as a callback to onFolderSelected in MoveToFolderPaneTree
     */
    onMoveToFolderItemSelected?: () => void;
}

const FOLDER_PANE_DIV_ID = 'FolderPaneDivId';

const moveHeaderText = isItemView() ? strings.moveMessage : strings.moveConversation;

@observer
class FolderPane extends React.Component<FolderPaneProps, FolderPaneState> {
    private timeoutRef: any = null;
    private scrollableItemRef = null;
    private headerRef = React.createRef<HTMLDivElement>();

    constructor(props: FolderPaneProps) {
        super(props);
        this.state = {
            currentHeight: null,
        };
        this.scrollableItemRef = React.createRef();
    }

    public isDogFoodFeedback = isMiniFeatureEnabled('mini-feedbackDF');

    private onReportErrorClick = () => {
        this.onCloseFolderPane();
        lazyShowHideReportErrorForm.importAndExecute(MiniWorkLoad.Mail, true);
    };

    public showReportErrorLink = this.isDogFoodFeedback && !!this.onReportErrorClick;
    public showFeedbackLink = shouldShowFeedbackLink();

    public totalLength =
        getMailRootFolderIds().length +
        store.orderedFavoritesFolderIds.length +
        4 + //For the two headers, and the two common buttons (Sign Out and Get Help)
        +(isDevToolsEnabled() ? 1 : 0) +
        (this.showReportErrorLink ? 1 : 0) +
        (this.showFeedbackLink ? 1 : 0);
    public itemRefs = Array.from({ length: this.totalLength }, () => React.createRef<any>());

    private renderFolderPaneList(): JSX.Element {
        const { showMoveToFolderPane } = store;
        const { UserOptions } = getUserConfiguration();

        if (showMoveToFolderPane) {
            // Folder list in Move to folder pane
            return (
                <MoveToFolderPaneTree
                    folderIds={getMailRootFolderIds()}
                    rootDisplayName={strings.folders}
                    folderTreeType={FolderTreeType.PrimaryFolderTree}
                    onFolderSelected={this.props.onMoveToFolderItemSelected}
                    isRootExpanded={!UserOptions.IsMailRootFolderTreeCollapsed}
                    itemRefs={this.itemRefs}
                    startIndex={store.orderedFavoritesFolderIds.length}
                />
            );
        } else {
            // Folder list in regular folder pane
            return (
                <FolderPaneTree
                    folderIds={getMailRootFolderIds()}
                    rootDisplayName={strings.folders}
                    folderTreeType={FolderTreeType.PrimaryFolderTree}
                    isRootExpanded={!UserOptions.IsMailRootFolderTreeCollapsed}
                    itemRefs={this.itemRefs}
                    startIndex={store.orderedFavoritesFolderIds.length + 1} //The + 1 is to account for the header of the folder drop down
                />
            );
        }
    }

    private renderFavoritesList(): JSX.Element {
        const { orderedFavoritesFolderIds, showMoveToFolderPane } = store;
        const { UserOptions } = getUserConfiguration();

        if (!(orderedFavoritesFolderIds && orderedFavoritesFolderIds.length)) {
            return null;
        }

        if (showMoveToFolderPane) {
            // Favorite list in Move to Folder pane.
            return (
                <MoveToFolderPaneTree
                    folderIds={orderedFavoritesFolderIds}
                    rootDisplayName={strings.favoritesHeaderText}
                    folderTreeType={FolderTreeType.Favorites}
                    onFolderSelected={this.props.onMoveToFolderItemSelected}
                    isRootExpanded={!UserOptions.IsFavoritesFolderTreeCollapsed}
                    itemRefs={this.itemRefs}
                    startIndex={0}
                    totalLength={this.totalLength}
                />
            );
        } else {
            // Favorite list in regular Folder pane.
            return (
                <FolderPaneTree
                    folderIds={orderedFavoritesFolderIds}
                    rootDisplayName={strings.favoritesHeaderText}
                    folderTreeType={FolderTreeType.Favorites}
                    isRootExpanded={!UserOptions.IsFavoritesFolderTreeCollapsed}
                    itemRefs={this.itemRefs}
                    startIndex={0}
                    totalLength={this.totalLength}
                />
            );
        }
    }

    setFolderPaneHeight = () => {
        // Bug: https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/33107
        // FolderPane earlier had a height equal to 100vh.
        // However this creates an issue in iOS/Safari where viewport height dynamically changes due to iOS bottom bar
        // The fix is to listen the window resize event and set the current height of folderpane to viewport height
        this.setState({ currentHeight: window.innerHeight });
    };

    componentDidMount() {
        const store = getFolderStore();
        // Set the current height of folder pane as the component is mounted.
        // This is necessary as we don't know yet the state of viewport (whether the bottom browser menu is visible in iOS/Safari)
        this.setFolderPaneHeight();
        window.addEventListener(RESIZE_EVENT, this.delayedTriggerToSetFolderPaneHeight);
        document.addEventListener('keydown', this.escapeKeyPressedHandler, true);
        if (store.perfDatapoint) {
            store.perfDatapoint.end();
            openLeftNavPerfDatapointInitializer(false);
        }
    }

    escapeKeyPressedHandler = e => {
        escapeKeyHandler(e => {
            // This is needed to prevent extension to getting closed when user presses escape key
            e.preventDefault();
            this.onCloseFolderPane();
        })(e);
    };

    delayedTriggerToSetFolderPaneHeight = () => {
        // Adding debounce as window.innerHeight in setFolderPaneHeight is a costly call.
        clearTimeout(this.timeoutRef);
        this.timeoutRef = setTimeout(this.setFolderPaneHeight, DEBOUNCE_DELAY);
    };

    folderPaneTreeBody = (showMoveToFolderPane: boolean) => {
        const closeIcon: HeaderIcon = {
            icon: MiniIcons.Cancel,
            onClick: toggleMoveToFolderPane,
            ariaLabel: strings.closeMoveActionPopupAriaLabel,
        };

        return (
            <PortalWrapper
                divOrIdToAttachTo={FOLDER_PANE_DIV_ID}
                classNameForDiv={classNames(
                    styles.folderPane,
                    mergeStyles({ height: this.state.currentHeight })
                )}>
                <Overlay
                    onClick={showMoveToFolderPane ? null : this.onCloseFolderPane}
                    className={styles.folderPaneBackground}
                />
                {showMoveToFolderPane ? (
                    <div className={styles.moveToFolderPaneWrapper} ref={this.scrollableItemRef}>
                        <Header
                            title={getHeaderTextWithSelectedItemsCount(moveHeaderText)}
                            leftIcon={closeIcon}
                            headerStyles={styles.moveToFolderHeader}
                            titleAriaLabel={getHeaderAriaLabel(moveHeaderText)}
                            ref={this.headerRef}
                            showBorder
                        />
                        {this.renderFavoritesList()}
                        {this.renderFolderPaneList()}
                    </div>
                ) : (
                    <>
                        <div
                            className={styles.folderPaneWrapper}
                            role="navigation"
                            aria-label={strings.folderNavigationButtonAriaLabel}
                            ref={this.scrollableItemRef}>
                            <SidePaneHeader
                                ref={this.headerRef}
                                showSettings
                                openSettingsCallback={this.onOpenSettings}
                            />
                            {this.renderFavoritesList()}
                            {this.renderFolderPaneList()}
                            <SidePaneFooter
                                onSidePaneClose={this.onCloseFolderPane}
                                onFeedbackClick={this.onMailFeedbackClick}
                                onReportErrorClick={this.onReportErrorClick}
                                itemRefs={this.itemRefs}
                                startIndex={
                                    store.orderedFavoritesFolderIds.length +
                                    getMailRootFolderIds().length +
                                    2
                                } //The plus two accounts for the favourite/ folder drop down list header
                                totalLength={this.totalLength}
                            />
                            <DiagnosticsTrigger
                                totalLength={this.totalLength}
                                startIndex={this.totalLength - 1}
                                itemRefs={this.itemRefs}
                                onClick={this.onDiagnosticsClick}
                            />
                        </div>
                    </>
                )}
            </PortalWrapper>
        );
    };

    render() {
        const { showMoveToFolderPane } = store;

        return (
            <div>
                {!showMoveToFolderPane ? (
                    <FocusTrapZone
                        id={FOLDER_PANE_DIV_ID}
                        focusOnOpen={this.headerRef}
                        restrictTabOrder>
                        {this.folderPaneTreeBody(showMoveToFolderPane)}
                    </FocusTrapZone>
                ) : (
                    <div> {this.folderPaneTreeBody(showMoveToFolderPane)} </div>
                )}
            </div>
        );
    }

    private onMailFeedbackClick = () => {
        switchModule(Module.Feedback);
        this.onCloseFolderPane();
    };

    private onDiagnosticsClick = () => {
        switchModule(Module.Diagnostics);
        this.onCloseFolderPane();
    };

    componentWillUnmount() {
        // Clearing the timeout and removing the resize listener in unmount call
        clearTimeout(this.timeoutRef);
        window.removeEventListener(RESIZE_EVENT, this.delayedTriggerToSetFolderPaneHeight);
        document.removeEventListener('keydown', this.escapeKeyPressedHandler, true);
    }

    private onCloseFolderPane = () => {
        setShowFolderPane(false /*showFolderPane*/);
    };

    private onOpenSettings = () => {
        logUsage('ChangeModuleOptions');
        switchModule(Module.Options);
        setShowFolderPane(false /*showFolderPane*/);
    };
}

export default preventModalScroll(
    FolderPane,
    (component: any): HTMLDivElement => component.scrollableItemRef.current
);
